import { FC } from 'react';
import clsx from 'clsx';
import { NoteListItemDto } from '../types/interface';
import { formatNoteDate } from '../mappers/noteMappers';

interface NoteListItemProps {
  item: NoteListItemDto;
  isSelected?: boolean;
  onClick?: () => void;
}

/**
 * Helper to lighten a hex color for background
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const NoteListItem: FC<NoteListItemProps> = ({ item, isSelected, onClick }) => {
  // Get display values
  const displayDate =
    item.displayDate || item.relativeTime || formatNoteDate(item.creationTime);
  const tags = item.tags || [];
  const description = item.contentExcerpt?.slice(0, 150) || '';

  return (
    <div
      onClick={onClick}
      className={clsx(
        'rounded-xl border p-4 transition-all duration-200 cursor-pointer',
        isSelected
          ? 'bg-primary/5 border-primary shadow-sm'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
      )}
    >
      {/* Title with priority indicator */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-base font-semibold text-slate-700 line-clamp-1 flex-1">
          {item.title}
        </h3>
        {item.priority && (
          <span
            className="w-4 h-4 rounded-full flex-shrink-0 border-[4px]"
            style={{
              borderColor: item.priority.color || '#94a3b8'
            }}
            title={item.priority.label || item.priority.code}
          />
        )}
      </div>

      {/* Description preview */}
      <p className="text-sm text-slate-400 line-clamp-2 mb-3">{description}</p>

      {/* Metadata row */}
      <div className="flex items-center justify-between">
        {/* Date */}
        <span className="text-xs text-slate-400">{displayDate}</span>

        {/* Tags */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {tags.slice(0, 3).map((tag) => (
            <span
              key={tag.id}
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={
                tag.color
                  ? {
                      backgroundColor: hexToRgba(tag.color, 0.15),
                      color: tag.color
                    }
                  : {
                      backgroundColor: '#f1f5f9',
                      color: '#475569'
                    }
              }
            >
              {tag.label}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-400">
              +{tags.length - 3}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoteListItem;
